/**
 * @fileoverview Model sync of a text, number or date filter cell
 *
 * Holds the complete rows the filter is made of; {@link useFilterModelSync} keeps them in step
 * with the grid's model.
 */

import type { MaybeRefOrGetter } from "vue";
import { computed, readonly, ref, toValue, watch } from "vue";

import { DEFAULT_FILTER_DATE_FORMAT, DEFAULT_FILTER_TIME_FORMAT } from "../constants";
import { useDateFilterFormat } from "../date/useDateFilterFormat";
import type { ColFilterGroup } from "../filterModel";
import { FilterOperator } from "../filterModel";
import type { ConditionColumnFilterConfig } from "../types";
import { useFilterModelSync } from "../useFilterModelSync";
import type { ConditionRow } from "./conditionChain";
import { buildConditionGroup, parseConditionGroup } from "./conditionChain";

export interface ConditionFilterOptions {
  colKey: MaybeRefOrGetter<string>;
  config: MaybeRefOrGetter<ConditionColumnFilterConfig>;
  model: MaybeRefOrGetter<ColFilterGroup[] | null>;
  onModelChange: (model: ColFilterGroup[] | null) => void;
}

export function useConditionFilter(options: ConditionFilterOptions) {
  const config = computed(() => toValue(options.config));
  const dateConfig = computed(() => (config.value.kind === "date" ? config.value : null));
  const defaultOperator = computed(() =>
    config.value.kind === "date" ? FilterOperator.EQUALS : config.value.defaultOperator,
  );

  const dateFormat = useDateFilterFormat({
    kind: () => dateConfig.value?.valueKind ?? "date",
    dateFormat: () => dateConfig.value?.dateFormat ?? DEFAULT_FILTER_DATE_FORMAT,
    timeFormat: () => dateConfig.value?.timeFormat ?? DEFAULT_FILTER_TIME_FORMAT,
    datePrecision: () => dateConfig.value?.datePrecision,
    timePrecision: () => dateConfig.value?.timePrecision,
  });

  const rows = ref<ConditionRow[]>([]);

  const sync = useFilterModelSync({
    model: options.model,
    onModelChange: options.onModelChange,
    build: () => {
      const colKey = toValue(options.colKey);
      const group = colKey ? buildConditionGroup(colKey, rows.value) : null;
      return group ? [group] : null;
    },
    adopt: (model) => {
      const colKey = toValue(options.colKey);
      const group = model?.find((entry) => entry.colKey === colKey) ?? model?.[0];
      rows.value = parseConditionGroup(group, {
        kind: config.value.kind,
        defaultOperator: defaultOperator.value,
        normalizeTerm: dateConfig.value ? dateFormat.normalizeTerm : undefined,
      });
    },
  });

  function change(next: readonly ConditionRow[]) {
    rows.value = [...next];
    sync.stage();
  }

  function clear() {
    rows.value = [];
    sync.clear();
  }

  watch(dateFormat.shape, () => {
    if (dateConfig.value) sync.readopt();
  });

  const formatTerm = computed(() => (dateConfig.value ? dateFormat.formatTerm : undefined));

  return {
    rows: readonly(rows),
    defaultOperator,
    formatTerm,
    change,
    flush: sync.flush,
    clear,
  };
}
